import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, MapPin, Star, Filter, X, ChevronDown, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import TradieCarousel from '../components/TradieCarousel';

import { API_URL } from '../utils/api';

const TradesBrowsePage = () => {
  const [tradies, setTradies] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    service: '',
    state: '',
    area: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const australianStates = [
    { value: 'NSW', label: 'New South Wales' },
    { value: 'VIC', label: 'Victoria' },
    { value: 'QLD', label: 'Queensland' },
    { value: 'WA', label: 'Western Australia' },
    { value: 'SA', label: 'South Australia' },
    { value: 'TAS', label: 'Tasmania' },
    { value: 'ACT', label: 'Australian Capital Territory' },
    { value: 'NT', label: 'Northern Territory' }
  ];

  useEffect(() => {
    fetchServices();
    fetchTradies();
  }, []);

  useEffect(() => {
    fetchTradies();
  }, [filters]);

  const fetchServices = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/trades/services`);
      setServices(res.data.services);
    } catch (err) {
      console.error('Failed to fetch services', err);
    }
  };

  const fetchTradies = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.service) params.append('service', filters.service);
      if (filters.state) params.append('state', filters.state);
      if (filters.area) params.append('area', filters.area);

      const res = await axios.get(`${API_URL}/api/trades/browse?${params.toString()}`);
      setTradies(res.data.tradies);
    } catch (err) {
      console.error('Failed to fetch tradies', err);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({ service: '', state: '', area: '' });
  };

  const getServiceName = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    return service ? service.name : serviceId;
  };

  const hasActiveFilters = filters.service || filters.state || filters.area;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-sky-500 to-teal-500 text-white py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Find Local Tradies</h1>
          <p className="text-sky-100">Browse skilled tradespeople in your area</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* Mobile Filter Toggle */}
          <div className="md:hidden mb-4">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="w-full justify-between"
            >
              <span className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Filters {hasActiveFilters && `(${[filters.service, filters.state, filters.area].filter(Boolean).length})`}
              </span>
              <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </Button>
          </div>

          {/* Filter Controls */}
          <div className={`${showFilters ? 'block' : 'hidden'} md:block`}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Select
                value={filters.service || "all"}
                onValueChange={(value) => setFilters({ ...filters, service: value === "all" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Services" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Services</SelectItem>
                  {services.map((service) => (
                    <SelectItem key={service.id} value={service.id}>
                      {service.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.state || "all"}
                onValueChange={(value) => setFilters({ ...filters, state: value === "all" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All States" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {australianStates.map((state) => (
                    <SelectItem key={state.value} value={state.value}>
                      {state.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                placeholder="Search area/suburb..."
                value={filters.area}
                onChange={(e) => setFilters({ ...filters, area: e.target.value })}
                className="h-10"
              />

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="text-stone-500 hover:text-stone-700"
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Featured Tradies Section - uses TradieCarousel for auto-scrolling */}
        <TradieCarousel />

        {/* All Tradies */}
        <div className="mb-4 mt-8">
          <h2 className="text-lg font-semibold text-stone-900">All Tradies</h2>
        </div>
        
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg border border-stone-200 p-6 animate-pulse">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-stone-200 rounded-full" />
                  <div className="flex-1">
                    <div className="h-5 bg-stone-200 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-stone-200 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : tradies.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-stone-400 mb-4">
              <Search className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-stone-900 mb-2">No tradies found</h3>
            <p className="text-stone-500 mb-4">Try adjusting your filters or search in a different area</p>
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <>
            <p className="text-sm text-stone-500 mb-6">{tradies.length} tradie{tradies.length !== 1 ? 's' : ''} found</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tradies.map((tradie, index) => (
                <motion.div
                  key={tradie.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link to={`/profile/${tradie.id}`}>
                    <div className="bg-white rounded-lg border border-stone-200 p-6 hover:shadow-md transition-shadow">
                      {/* Header */}
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-16 h-16 rounded-full bg-stone-100 overflow-hidden flex-shrink-0">
                          {tradie.profile_photo ? (
                            <img
                              src={tradie.profile_photo}
                              alt={tradie.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-stone-400">
                              {tradie.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-stone-900 truncate">{tradie.name}</h3>
                          <div className="flex items-center gap-1 text-sm text-stone-500">
                            <MapPin className="h-3.5 w-3.5" />
                            <span className="truncate">
                              {tradie.trade_service_area && `${tradie.trade_service_area}, `}
                              {tradie.trade_service_state || tradie.location || 'Australia'}
                            </span>
                          </div>
                          {tradie.average_rating && (
                            <div className="flex items-center gap-1 mt-1">
                              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                              <span className="text-sm font-medium">{tradie.average_rating}</span>
                              <span className="text-sm text-stone-400">({tradie.review_count})</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Services */}
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {tradie.trade_services.slice(0, 3).map((serviceId) => (
                          <span
                            key={serviceId}
                            className="px-2 py-0.5 bg-teal-50 text-teal-600 text-xs rounded-full"
                          >
                            {getServiceName(serviceId)}
                          </span>
                        ))}
                        {tradie.trade_services.length > 3 && (
                          <span className="px-2 py-0.5 bg-stone-100 text-stone-600 text-xs rounded-full">
                            +{tradie.trade_services.length - 3} more
                          </span>
                        )}
                      </div>

                      {/* Description Preview */}
                      {tradie.trade_description && (
                        <p className="text-sm text-stone-600 line-clamp-2 mb-4">
                          {tradie.trade_description}
                        </p>
                      )}

                      {/* Portfolio Preview */}
                      {tradie.trade_photos && tradie.trade_photos.length > 0 && (
                        <div className="flex gap-1 overflow-hidden rounded">
                          {tradie.trade_photos.slice(0, 4).map((photo, i) => (
                            <div
                              key={i}
                              className="w-1/4 aspect-square bg-stone-100 overflow-hidden"
                            >
                              <img
                                src={photo}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TradesBrowsePage;
